import type { LeaderTier, OrganisationInvestor, OrganisationLeader } from '@ni/shared';

/**
 * **One person per name on the People tab.** The wire lists one row per fact, so
 * somebody with a job and a board seat, or an investor in two rounds, arrives
 * twice. The tab draws each of them once with every line under the name.
 */

export interface LeaderCard {
  person_entity_id: string;
  display_name: string;
  tracked: boolean;
  /** The best band any of their roles ranks in. */
  tier: LeaderTier;
  roles: Pick<OrganisationLeader, 'role' | 'since' | 'fact_type'>[];
}

export interface InvestorCard {
  investor: OrganisationInvestor;
  rounds: OrganisationInvestor[];
}

/** Server order is kept: the first row for a person is their best-ranked one. */
export function leaderCards(leadership: readonly OrganisationLeader[]): LeaderCard[] {
  const byPerson = new Map<string, LeaderCard>();
  for (const leader of leadership) {
    let card = byPerson.get(leader.person_entity_id);
    if (!card) {
      card = {
        person_entity_id: leader.person_entity_id,
        display_name: leader.display_name,
        tracked: leader.tracked,
        tier: leader.tier,
        roles: [],
      };
      byPerson.set(leader.person_entity_id, card);
    }
    const same = card.roles.some(
      (r) => r.role === leader.role && r.since === leader.since && r.fact_type === leader.fact_type,
    );
    if (!same)
      card.roles.push({ role: leader.role, since: leader.since, fact_type: leader.fact_type });
  }
  for (const card of byPerson.values()) {
    // A job with no title or date says nothing a stated role beside it does not.
    if (card.roles.length > 1) {
      const stated = card.roles.filter((r) => r.role || r.since || r.fact_type === 'board_seat');
      if (stated.length > 0) card.roles = stated;
    }
  }
  return [...byPerson.values()];
}

export function investorCards(investors: readonly OrganisationInvestor[]): InvestorCard[] {
  const byEntity = new Map<string, InvestorCard>();
  for (const investor of investors) {
    const card = byEntity.get(investor.entity_id);
    if (card) card.rounds.push(investor);
    else byEntity.set(investor.entity_id, { investor, rounds: [investor] });
  }
  return [...byEntity.values()];
}

/** The bands in reading order. The last two fold behind "Show more". */
export const LEADER_TIER_ORDER: readonly LeaderTier[] = [
  'principal',
  'executive',
  'senior',
  'staff',
  'support',
];
const FOLDED_TIERS: ReadonlySet<LeaderTier> = new Set(['staff', 'support']);

export interface LeaderGroup {
  tier: LeaderTier;
  cards: LeaderCard[];
  folded: boolean;
}

/** A list this short is shown whole: folding it would hide less than it costs. */
const FOLD_ABOVE = 12;

/**
 * The non-empty bands, in order. Staff and support fold only in a long list
 * where a band above them has somebody in it; a list of nothing but staff is
 * shown whole.
 */
export function leaderGroups(cards: readonly LeaderCard[]): LeaderGroup[] {
  const groups = LEADER_TIER_ORDER.map((tier) => ({
    tier,
    cards: cards.filter((card) => card.tier === tier),
    folded: false,
  })).filter((group) => group.cards.length > 0);
  const anyLeading = groups.some((group) => !FOLDED_TIERS.has(group.tier));
  if (cards.length <= FOLD_ABOVE) return groups;
  if (anyLeading) for (const group of groups) group.folded = FOLDED_TIERS.has(group.tier);
  return groups;
}
